import type { IsoString } from '../types/index.js';
import { formatRelativeTime } from '../utils/index.js';

// AlertDisplay is a denormalized view model — it combines Alert fields with
// the resolved device/building names needed for display. When the alerts API
// is connected, build these from Alert + Device + Entity lookups.
export interface AlertDisplay {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  deviceName: string;
  buildingName: string;
  room: string;
  createdAt: IsoString;
  status: 'active' | 'acknowledged' | 'resolved';
}

interface AlertBannerBarProps {
  alerts: AlertDisplay[];
  readOnly?: boolean;
  onAcknowledge?: (alertId: string) => void;
}

const SEVERITY_CONFIG = {
  critical: {
    label: 'CRITICAL',
    border: 'border-l-danger',
    text: 'text-danger',
    dot: 'bg-danger',
    ring: 'ring-danger/20',
  },
  warning: {
    label: 'WARNING',
    border: 'border-l-warning',
    text: 'text-warning',
    dot: 'bg-warning',
    ring: 'ring-warning/20',
  },
  info: {
    label: 'INFO',
    border: 'border-l-info',
    text: 'text-info',
    dot: 'bg-info',
    ring: 'ring-info/20',
  },
} as const;

const MAX_VISIBLE = 4;

export function AlertBannerBar({ alerts, readOnly = false, onAcknowledge }: AlertBannerBarProps) {
  const active = alerts.filter((a) => a.status === 'active');

  // Sort: critical first, then warning, then info, newest first within each group
  const sorted = [...active].sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    if (order[a.severity] !== order[b.severity]) {
      return order[a.severity] - order[b.severity];
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  if (sorted.length === 0) return null;

  const visible = sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - MAX_VISIBLE;

  return (
    <section
      aria-label="Active alerts"
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-2.5 pointer-events-none"
      style={{ width: '340px' }}
    >
      {visible.map((alert) => {
        const cfg = SEVERITY_CONFIG[alert.severity];
        return (
          <div
            key={alert.id}
            role="alert"
            aria-live="assertive"
            className={`
              pointer-events-auto
              flex items-start gap-3
              rounded-xl border border-border border-l-4 ${cfg.border}
              bg-bg-surface shadow-lg ring-1 ${cfg.ring}
              px-4 py-3
            `}
          >
            {/* Severity dot */}
            <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${cfg.dot}`} aria-hidden="true" />

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className={`text-xs font-bold tracking-wide ${cfg.text}`}>{cfg.label}</span>
                <span className="truncate text-sm font-medium text-text-primary">
                  {alert.deviceName}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-text-primary leading-snug">{alert.message}</p>
              <p className="mt-1 text-xs text-text-secondary">
                {alert.buildingName} &rsaquo; {alert.room}
                &nbsp;·&nbsp;
                {formatRelativeTime(alert.createdAt)}
              </p>
            </div>

            {/* Acknowledge button */}
            {!readOnly && onAcknowledge && (
              <button
                type="button"
                onClick={() => onAcknowledge(alert.id)}
                aria-label={`Acknowledge alert: ${alert.message}`}
                className="shrink-0 -mr-1 -mt-0.5 rounded-lg p-1.5 text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {/* × close icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      })}

      {/* Overflow badge */}
      {overflow > 0 && (
        <div className="pointer-events-auto self-end rounded-full bg-bg-elevated px-3 py-1 text-xs font-medium text-text-secondary shadow ring-1 ring-border">
          +{overflow} more alert{overflow === 1 ? '' : 's'}
        </div>
      )}
    </section>
  );
}
